import { Row, Col, Badge } from 'reactstrap';
import { PageHeader } from '../../components/PageHeader';
import { DataTable } from '../../components/DataTable';
import { RelativeTime } from '../../components/RelativeTime';
import { KpiCard } from '../../components/KpiCard';
import { useBackupStats } from './useBackupStats';

/**
 * Produkt-Statistik Backups — Betreiber-Sicht, nur lesend. KPI-Kacheln oben,
 * darunter Runs nach Status (30 Tage) und die letzten Läufe.
 */
const fmt = new Intl.NumberFormat('de-DE');

function humanBytes(bytes) {
  if (bytes == null) return '—';
  if (bytes === 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB', 'PB'];
  const i = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  return `${(bytes / 1024 ** i).toFixed(i ? 1 : 0)} ${units[i]}`;
}

function statusBadge(status) {
  if (status === 'ok') return <Badge color="success">ok</Badge>;
  if (status === 'error') return <Badge color="danger">error</Badge>;
  return <Badge color="secondary">{status}</Badge>;
}

export function NexusBackupStatsPage() {
  const stats = useBackupStats();
  const data = stats.data ?? {};
  const plans = data.plans ?? {};
  const byStatus = data.byStatus ?? [];
  const recent = data.recent ?? [];

  return (
    <div>
      <PageHeader title="Backups" subtitle="Produkt-Statistik — Ziele, Pläne und Backup-Läufe" />

      <Row className="g-3 mb-3">
        <Col xs="6" md="4" lg="2"><KpiCard label="Ziele" value={fmt.format(data.targets ?? 0)} /></Col>
        <Col xs="6" md="4" lg="2"><KpiCard label="Pläne" value={fmt.format(plans.total ?? 0)} /></Col>
        <Col xs="6" md="4" lg="2"><KpiCard label="davon aktiv" value={fmt.format(plans.enabled ?? 0)} /></Col>
        <Col xs="6" md="4" lg="2"><KpiCard label="Plattform-Ziel" value={fmt.format(plans.platform ?? 0)} /></Col>
        <Col xs="6" md="4" lg="2"><KpiCard label="DB-Pläne" value={fmt.format(plans.db ?? 0)} /></Col>
        <Col xs="6" md="4" lg="2"><KpiCard label="Runs gesamt" value={fmt.format(data.runs ?? 0)} /></Col>
      </Row>

      <Row className="g-3">
        <Col xs={12} lg={4}>
          <div className="text-muted small text-uppercase mb-2">Runs nach Status (30 Tage)</div>
          <DataTable
            loading={stats.isLoading}
            error={stats.error}
            empty={!stats.isLoading && byStatus.length === 0}
            emptyMessage="Keine Runs im Zeitraum."
            onRetry={() => stats.refetch()}
          >
            <thead>
              <tr>
                <th>Status</th>
                <th className="text-end">Runs</th>
              </tr>
            </thead>
            <tbody>
              {byStatus.map((r) => (
                <tr key={r.status}>
                  <td>{statusBadge(r.status)}</td>
                  <td className="text-end small">{fmt.format(r.n ?? 0)}</td>
                </tr>
              ))}
            </tbody>
          </DataTable>
        </Col>

        <Col xs={12} lg={8}>
          <div className="text-muted small text-uppercase mb-2">Letzte 20 Läufe</div>
          <DataTable
            loading={stats.isLoading}
            error={stats.error}
            empty={!stats.isLoading && recent.length === 0}
            emptyMessage="Keine Läufe."
            onRetry={() => stats.refetch()}
          >
            <thead>
              <tr>
                <th>ID</th>
                <th>Plan</th>
                <th>Status</th>
                <th>Snapshot</th>
                <th className="text-end">Größe</th>
                <th style={{ width: 120 }}>Gestartet</th>
                <th style={{ width: 120 }}>Beendet</th>
              </tr>
            </thead>
            <tbody>
              {recent.map((r) => (
                <tr key={r.id}>
                  <td className="font-monospace small">{r.id}</td>
                  <td className="font-monospace small">{r.plan_id}</td>
                  <td>{statusBadge(r.status)}</td>
                  <td className="font-monospace small">{r.snapshot_id || '—'}</td>
                  <td className="text-end small">{humanBytes(r.bytes)}</td>
                  <td>{r.started_at ? <RelativeTime iso={r.started_at} /> : '—'}</td>
                  <td>{r.finished_at ? <RelativeTime iso={r.finished_at} /> : '—'}</td>
                </tr>
              ))}
            </tbody>
          </DataTable>
        </Col>
      </Row>
    </div>
  );
}
